import { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";

import useAxios from "../../../utils/useAxios";
import { resObj, paginationVal } from "../../../utils/constants";
import PermissionContext from "../../../context/PermissionContext";
import DrawerContext from "../../../context/DrawerContext";
import AutoLogout from "../../../utils/useAutoLogout";

import Pagination from "@mui/material/Pagination";

import SideDrawer from "../../../components/layout/SideDrawer";
import ListHeader from "../../../components/layout/ListHeader";
import CustomTable from "../../../components/layout/CustomTable";
import NoData from "../../../components/layout/NoData";
import SuccessAlert from "../../../components/layout/SuccessAlert";
import ErrorModal from "../../../components/layout/ErrorModal";
import DeleteModal from "../../../components/layout/DeleteModal";
import Loader from "../../../components/layout/Loader";

const BuffersListPage = () => {
  const api = useAxios();
  const navigate = useNavigate();

  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  useEffect(() => {
    if (permisionsList.length > 0) checkUserPermission(['Materials Capture-Buffers']);
  }, [permisionsList]);

  const [apiRes, setApiRes] = useState(resObj);
  const [buffersList, setBuffersList] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [lipidId, setLipidId] = useState('');

  const pageData = useRef({ ...paginationVal });

  const tableHeading = ['SI No', 'Unique ID', 'Buffer Acronym', 'Date Prepared', 'Components'];

  const deleteModalTitle = 'Lipid Raw Material Delete';
  const deleteModalMessage = `Are you sure you want to delete? 
    (All related informations will be lost)`;

  useEffect(() => {
    getBuffers();
  }, []);

  const handlePaginationClick = (e, pageNumber) => {
    pageData.current.pageNumber = pageNumber;
    getBuffers();
  };

  const navigateToBufferCreate = () => {
    navigate('/buffer/create');
  };

  const navigateToBufferEdit = id => {
    navigate(`/buffer/edit/${id}`);
  };

  const getBuffers = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      const response = await api.get(
        '/api/materials-capture/buffer/list/get', {
        params: {
          search_term: pageData.current.searchTerm,
          page_number: +pageData.current.pageNumber,
        }
      });

      if (response.status === 200) {
        setApiRes({ ...apiRes, loading: false });
        setBuffersList(response.data.data);
        pageData.current.pageCount = response.data.page_count;
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Buffer List Get',
        loading: false,
      });
    }
  };

  const deleteBuffer = async () => {
    try {
      setApiRes({ ...apiRes, loading: true });
      setDeleteModalOpen(false);
      const response = await api.delete(
        `/api/materials-capture/buffer/delete/${lipidId}`
      );

      if (response.status === 200) {
        getBuffers();
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'Buffer deleted successfully',
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Buffer Delete',
        loading: false,
      });
    }
  };

  return (
    <AutoLogout>
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      {apiRes.loading && <Loader />}
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <ListHeader
          title="Buffers"
          handleClick={navigateToBufferCreate}
          search={getBuffers}
          pageData={pageData}
          hasCreatePermission={permisionsList.includes('Materials Capture-Buffers')}
        />

        <CustomTable
          heading={tableHeading}
          rows={buffersList}
          navigateToEdit={navigateToBufferEdit}
          setDeleteModalOpen={setDeleteModalOpen}
          setId={setLipidId}
          pageData={pageData}
          idKey={'id'}
          isDeleteBtnVisible={permisionsList.includes('Admin-Manage Role')}
          showToolTip={false}
        />

        {buffersList.length === 0 && <NoData />}

        {(buffersList.length !== 0 && pageData.current.pageCount !== 1) && <Pagination
          style={{ marginTop: '10px', float: 'right' }}
          count={pageData.current.pageCount}
          onChange={handlePaginationClick} />}

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />

        <DeleteModal
          title={deleteModalTitle}
          message={deleteModalMessage}
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDelete={deleteBuffer}
        />
      </div>
    </AutoLogout>
  );
};

export default BuffersListPage;